import { useCallback, useEffect, useRef } from 'react';

import { useClickOutside } from '@/hooks/useClickOutside';

import type { RefObject } from 'react';
import type { FormSubsection } from '@/types/formSections.types';

/**
 * Hook to provide scrollToSection and highlight logic for forms with sections.
 * @param getRef - function to get a ref for a sectionId
 * @param setActiveSubsection - setter for the active subsection
 * @param containerRef - ref to the scrollable container
 * @returns { scrollToSection }
 */
export function useHighlightSection(
  getRef: (sectionId: string) => RefObject<HTMLElement> | null,
  setActiveSubsection: (section: FormSubsection | null) => void,
  containerRef: RefObject<HTMLElement>,
  activeSubsectionId: string | null
) {
  const isProgrammaticScrollRef = useRef(false);
  const activeSubsectionRef = getRef(activeSubsectionId ?? '');

  const scrollToSection = useCallback(
    (sectionId: string) => {
      const sectionRef = getRef(sectionId);
      if (sectionRef?.current) {
        isProgrammaticScrollRef.current = true;
        setActiveSubsection(sectionId as FormSubsection);
        sectionRef.current.scrollIntoView({ behavior: 'smooth' });
        setTimeout(() => {
          isProgrammaticScrollRef.current = false;
        }, 1000);
      }
    },
    [getRef, setActiveSubsection]
  );

  const removeActiveSubsection = useCallback(() => {
    if (!isProgrammaticScrollRef.current && activeSubsectionId) {
      setActiveSubsection(null);
    }
  }, [activeSubsectionId, setActiveSubsection]);

  useEffect(() => {
    const el = containerRef.current;
    if (el) {
      el.addEventListener('scroll', removeActiveSubsection);
    }
    return () => {
      if (el) {
        el.removeEventListener('scroll', removeActiveSubsection);
      }
    };
  }, [containerRef, setActiveSubsection]);

  useClickOutside(activeSubsectionRef, removeActiveSubsection);

  return { scrollToSection };
}
